import {
  isDefinedArray,
  isDefinedString,
  isNullOrUndefined,
  isPositiveNumber
} from "et3-type-guards";
import queryString from "query-string";
import { memoize } from "lodash";
import orderBy from "lodash/orderBy";
import { computed, observable, runInAction } from "mobx";

import { apiCallGetAuditedEventsToSupervise } from "../../../api/supervisor-api/api-call-get-audited-events-to-supervise";
import { apiCallSaveAuditedEventAsSupervised } from "../../../api/supervisor-api/api-call-save-audited-event-as-supervised";
import { getSpecificDateWithDays } from "../../../core/et3-date";
import { TAuditEvent, TIssueType } from "../../../Types";
import getTBWStore from "../../developer-store/tbw-store";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import i18n from "../../../constants/i18n";
import { TEt3PickerSingleValue } from "../../../TypesNEW";
import {
  reducerToEmptyStringIfUndefined,
  reducerToZeroIfUndefined
} from "../../../helpers/et3-picker-data-helper";
import { getExtraDataParts } from "../../../helpers/et3-get-event-list";

class SuperviseStore {
  auditedEventsToSupervise = observable.box<TAuditEvent[]>([]);
  adminInput = observable.box<TEt3PickerSingleValue>({
    value: "",
    label: i18n.get("CHOOSE")
  });

  typeInputAuditedEvent = observable.box<TEt3PickerSingleValue>({
    label: "All",
    value: "all"
  });
  typeInputAuditedIssue = observable.box<TEt3PickerSingleValue>({
    label: "All",
    value: "all"
  });
  subscriptionInput = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  genderInput = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  actionTakenInput = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  dateMinInput = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 1, daysAdded: 0 })
  );
  dateMaxInput = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 0, daysAdded: 1 })
  );
  limitInput = observable.box<TEt3PickerSingleValue>({
    label: "50",
    value: "50"
  });
  auditedEventsToSuperviseOrdered = computed(() => {
    const auditedEventsToSuperviseOrdered = orderBy(
      this.auditedEventsToSupervise.get(),
      ["audited_on"],
      ["desc"]
    );
    return auditedEventsToSuperviseOrdered.map((data) => {
      const formattedExtraDataArray = getExtraDataParts({
        eventType: data.type,
        description: data.description,
        reportID: data.report_id,
        investigationCaseID: data.investigation_case_id,
        temporaryBanUntil: data.temporary_ban_until,
        temporaryBanNumberOfDays: data.temporary_ban_number_of_days,
        extraData: data.extra_data
      });

      return {
        ...data,
        supervise_extra_data: formattedExtraDataArray.join(" ")
      };
    });
  });
  setAdmin = (admin: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.adminInput.set(admin);
    });
  };
  setTypeInputAuditedEvent = (type: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.typeInputAuditedEvent.set(type);
    });
  };
  setTypeInputAuditedIssue = (type: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.typeInputAuditedIssue.set(type);
    });
  };
  setSubscriptionInput = (subscription: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.subscriptionInput.set(subscription);
    });
  };
  setGenderInput = (gender: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.genderInput.set(gender);
    });
  };
  setActionTakenInput = (actionTaken: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.actionTakenInput.set(actionTaken);
    });
  };
  setLimitInput = (limit: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.limitInput.set(limit);
    });
  };

  isGetButtonEnabled = computed(() => {
    const eventType = this.typeInputAuditedEvent.get().value;
    const actionTaken = this.actionTakenInput.get().value;
    const isRestrictedEvent =
      eventType !== "report" && eventType !== "investigation_cases";
    const hasRestrictedFilter = isDefinedString(actionTaken);
    const isEnabled = isPositiveNumber(
      reducerToZeroIfUndefined(
        parseInt(getSuperviseStore().adminInput.get().value, 10)
      )
    );
    return isEnabled && !(isRestrictedEvent && hasRestrictedFilter);
  });

  checkURLSearch = async (search: string) => {
    if (isDefinedString(search)) {
      const eventName = queryString.parse(search).eventName;
      const issueType = queryString.parse(search).issueType;
      const dateMin = queryString.parse(search).dateMin[0];
      const dateMax = queryString.parse(search).dateMax[0];
      const adminId = parseInt(`${queryString.parse(search).adminID}`, 10);

      if (isDefinedString(eventName) && isDefinedString(issueType)) {
        //@webTeam fix admin name
        getSuperviseStore().setAdmin({
          value: adminId.toString(),
          label: ""
        });
        getSuperviseStore().typeInputSUAuditedEventSetVal(eventName);
        getSuperviseStore().typeInputSUAuditedEventSetVal(issueType);
        getSuperviseStore().dateMinInput.set(new Date(dateMin));
        getSuperviseStore().dateMaxInput.set(new Date(dateMax));
        await getSuperviseStore().getAuditedEventsToSupervise(true);
      }
    }
  };

  typeInputSUAuditedEventSetVal = (eventName: string) => {
    switch (eventName) {
      case "approval_description":
        this.setTypeInputAuditedEvent({
          label: "Approve/Reject Description",
          value: "approval_description"
        });
        break;
      case "approval_peer_description":
        this.setTypeInputAuditedEvent({
          label: "Approve/Reject Peer Description",
          value: "approval_peer_description"
        });
        break;
      case "approval_picture":
        this.setTypeInputAuditedEvent({
          label: "Approve/Reject Pictures",
          value: "approval_picture"
        });
        break;
      case "report":
        this.setTypeInputAuditedEvent({
          label: "Report",
          value: "report"
        });
        break;
      case "all":
        this.setTypeInputAuditedEvent({
          label: "All",
          value: "all"
        });
        break;
    }
  };

  typeInputSUAuditedIssueSetVal = (issueType: string) => {
    switch (issueType) {
      case "no_issue":
        this.setTypeInputAuditedIssue({
          label: "No Issue",
          value: "no_issue"
        });
        break;
      case "minor":
        this.setTypeInputAuditedIssue({
          label: "Minor",
          value: "minor"
        });
        break;
      case "major":
        this.setTypeInputAuditedIssue({
          label: "Major",
          value: "major"
        });
        break;
      case "all":
        this.setTypeInputAuditedIssue({
          label: "All",
          value: "all"
        });
        break;
    }
  };

  getAuditedEventsToSupervise = async (
    isAuditingPerformance: boolean = false
  ) => {
    const definedAdminID = reducerToZeroIfUndefined(
      parseInt(this.adminInput.get().value, 10)
    );
    const definedLimit = reducerToZeroIfUndefined(
      parseInt(this.limitInput.get().value, 10)
    );
    const type = this.typeInputAuditedEvent.get().value;
    const subscription = reducerToEmptyStringIfUndefined(
      this.subscriptionInput.get().value
    );
    const gender = reducerToEmptyStringIfUndefined(
      this.genderInput.get().value
    );
    const actionTaken = reducerToEmptyStringIfUndefined(
      this.actionTakenInput.get().value
    );

    if (!isPositiveNumber(definedAdminID) || !isPositiveNumber(definedLimit)) {
      return;
    }
    getCoreUIStore().setIsLoading("SUPERVISOR_SUPERVISE", true);
    const { data, status, code } = await apiCallGetAuditedEventsToSupervise({
      isAuditingPerformance: isAuditingPerformance,
      adminId: definedAdminID,
      dateMin: this.dateMinInput.get(),
      dateMax: this.dateMaxInput.get(),
      eventType: type,
      issueType: this.typeInputAuditedIssue.get().value,
      subscription,
      gender,
      actionTaken,
      limit: definedLimit
    });
    getCoreUIStore().setIsLoading("SUPERVISOR_SUPERVISE", false);
    if (status === "OK") {
      if (!isNullOrUndefined(data) && isDefinedArray(data)) {
        this.auditedEventsToSupervise.set(data);
      } else {
        this.auditedEventsToSupervise.set([]);
        getCorePopUpStore().showAlertMessage("EVENTS_NOT_FOUND");
      }
    } else {
      this.auditedEventsToSupervise.set([]);
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  saveAuditedEventAsSupervised = async ({
    eventID,
    issueType
  }: {
    eventID: number;
    issueType: TIssueType;
  }) => {
    const reason_ids = getTBWStore().getTWBValuesArray();
    const proof = getTBWStore().superviseEventProof.get();
    await apiCallSaveAuditedEventAsSupervised({
      eventID,
      issueType,
      reason_ids,
      proof
    });
    const events = this.auditedEventsToSupervise.get();
    const newEvents = events.filter((event) => event.id !== eventID);
    this.auditedEventsToSupervise.set(newEvents);
    getTBWStore().resetTBWPopUp();
  };
}
export const getSuperviseStore = memoize(
  () => new SuperviseStore(),
  () => 1
);
export default getSuperviseStore;
